import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, isDevMode, PLATFORM_ID } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { CheckoutDataService } from '@app/services';
import { RoutingConfigService } from 'haba-core';
import { CheckoutDetailsService, CheckoutStepService } from 'haba-featurelibs/checkout/components';
import { CheckoutStep, CheckoutStepType } from 'haba-featurelibs/checkout/root';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class HabaCheckoutStepsSetGuard {
  constructor(
    protected checkoutStepService: CheckoutStepService,
    protected checkoutDetailsService: CheckoutDetailsService,
    protected routingConfigService: RoutingConfigService,
    protected router: Router,
    private checkoutDataService: CheckoutDataService,
    @Inject(PLATFORM_ID) protected platform_id
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    _: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    if (isPlatformBrowser(this.platform_id)) {
      // CSR
      let currentIndex = -1;
      const currentRouteUrl = '/' + route.url.join('/');

      return this.checkoutStepService.steps$.pipe(
        take(1),
        switchMap((steps) => {
          currentIndex = steps.findIndex((step) => {
            const stepRouteUrl = `/${this.routingConfigService.getRouteConfig(step.routeName).paths?.[0]}`;
            return stepRouteUrl === currentRouteUrl;
          });
          // get current step
          let currentStep;
          if (currentIndex >= 0) {
            currentStep = steps[currentIndex];
          }
          if (currentStep) {
            return this.isStepSet(steps[currentIndex - 1]);
          } else {
            if (isDevMode()) {
              console.warn(
                `Missing step with route '${currentRouteUrl}' in checkout configuration or this step is disabled.`
              );
            }
            return of(this.getRouteUrl('checkout'));
          }
        })
      );
    } else {
      // SSR
      return of(true);
    }
  }

  protected isStepSet(step: CheckoutStep): Observable<boolean | UrlTree> {
    if (step && !step.disabled) {
      switch (step.type[0]) {
        case CheckoutStepType.SHIPPING_ADDRESS: {
          return this.isShippingAddressSet(step);
        }
        case CheckoutStepType.PAYMENT_DETAILS: {
          return this.isPaymentDetailsSet(step);
        }
        case CheckoutStepType.REVIEW_ORDER: {
          break;
        }
      }
    }
    return of(true);
  }

  protected isShippingAddressSet(step: CheckoutStep): Observable<boolean | UrlTree> {
    return this.checkoutDataService.getCheckoutData().pipe(
      map(({ deliveryAddress }) => {
        const canProceed = Boolean(
          Object.keys(deliveryAddress).length && deliveryAddress.addressType
        );
        return canProceed || this.getRouteUrl(step.routeName);
      })
    );
  }

  protected isPaymentDetailsSet(step: CheckoutStep): Observable<boolean | UrlTree> {
    return this.checkoutDataService.getCheckoutData().pipe(
      map((data) => {
        const canProceed = Boolean(
          data.paymentDetails?.isValid &&
            data.paymentDetails?.id &&
            data.paymentDetails?.billingAddress?.billingAddress
        );
        return canProceed || this.getRouteUrl(step.routeName);
      })
    );
  }

  private getRouteUrl(routeName: string): UrlTree {
    return this.router.parseUrl(
      this.routingConfigService.getRouteConfig(routeName).paths?.[0] as string
    );
  }
}
